import { Component, type ErrorInfo, type ReactNode } from "react";

import { Chevron } from "@/components/ui/Chevron";

/**
 * Top-level error boundary.
 *
 * React 18 still requires a class component for `componentDidCatch`.
 * We render a sober fallback with a "reload" affordance and log the
 * error to the console (and to App Insights in 3.5 once that's wired).
 */
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught", error, info);
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }
    const message = this.state.error.message || "Something went wrong.";
    return (
      <div className="mx-auto max-w-xl py-20 text-center space-y-4">
        <p className="font-mono text-xs uppercase tracking-widest text-destructive">
          <Chevron className="mr-2 text-destructive" /> unexpected error
        </p>
        <h1 className="text-2xl font-semibold">Something broke client-side.</h1>
        <p className="text-sm text-muted-foreground break-words">{message}</p>
        <div className="flex justify-center gap-3 pt-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Reload
          </button>
          <button
            type="button"
            onClick={this.reset}
            className="rounded-md border border-border bg-transparent px-4 py-2 text-sm font-medium hover:bg-secondary/60 transition-colors"
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }
}
